// The off-origin links of a store app (mobile-apps.md §8, R2927).
//
// The WKWebView of the iOS app shell opens no new window, so a link to
// another origin (Discord, the press links, a mail link) goes to Safari
// through the `open` op of the bridge. A link inside the origin opens in
// place there, even when it asks for a new tab: the app shell sends every
// new window to Safari, and the player must stay in the app (R2935). A browser and the Android TWA keep their own link
// behaviour: Chrome opens an off-origin link by itself.
//
// `openLink` replaces `window.open` at a call site. `installLinkBridge`
// catches the press on an anchor to another origin, so an anchor of any
// panel needs no change.

import { bridgeHas, bridgeSend, pageWindow, type BridgeWindow } from './bridge';

/** The schemes the app shell hands to Safari. */
const OFF_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * The absolute URL of `href` when it leads to another origin, or null for
 * a link inside the origin and for a scheme that is no page (a
 * `javascript:` or `blob:` link).
 */
export function offOrigin(href: string, origin: string, base: string = origin): string | null {
  let url: URL;
  try {
    url = new URL(href, base);
  } catch {
    return null;
  }
  if (!OFF_SCHEMES.has(url.protocol)) return null;
  return url.origin === origin ? null : url.toString();
}

/** The parts of the page that `openLink` reads. A test passes a fake. */
export interface LinkHost {
  readonly origin: string;
  readonly bridge: BridgeWindow | null;
  open(url: string): void;
  /** Loads `url` in place of the page. */
  go(url: string): void;
}

/** The page itself. */
function pageHost(): LinkHost {
  return {
    origin: location.origin,
    bridge: pageWindow(),
    open: (url) => { window.open(url, '_blank', 'noopener'); },
    go: (url) => { location.assign(url); },
  };
}

/**
 * Opens `href` in a tab of its own. Where the iOS app shell knows the
 * `open` op, a link that leaves the origin goes through it, and a link
 * inside the origin loads in place. Elsewhere it uses `window.open`.
 */
export function openLink(href: string, host: LinkHost = pageHost()): void {
  if (bridgeHas('open', host.bridge)) {
    const off = offOrigin(href, host.origin);
    if (off === null) { host.go(href); return; }
    if (bridgeSend('open', { url: off }, host.bridge)) return;
  }
  host.open(href);
}

/**
 * Catches a press on an anchor to another origin and sends it through the
 * `open` op. An anchor inside the origin that asks for a new tab loads in
 * place. It does nothing where the app shell does not know the op. It
 * answers the function that removes the listener.
 */
export function installLinkBridge(doc: Document = document,
  bridge: BridgeWindow | null = pageWindow()): () => void {
  if (!bridgeHas('open', bridge)) return () => {};
  const origin = doc.location?.origin ?? '';
  const onClick = (ev: MouseEvent): void => {
    if (ev.defaultPrevented || ev.button !== 0) return;
    const a = (ev.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a) return;
    const off = offOrigin(a.getAttribute('href') ?? '', origin, doc.baseURI);
    if (off === null) {
      if (a.target !== '_blank') return;
      ev.preventDefault();
      doc.location.assign(a.href);
      return;
    }
    if (bridgeSend('open', { url: off }, bridge)) ev.preventDefault();
  };
  doc.addEventListener('click', onClick, true);
  return () => doc.removeEventListener('click', onClick, true);
}
